import { z } from 'zod';
import apiClient from '../client';

// Certificate schemas
const certificateAccountSchema = z.object({
  accountName: z.string(),
  platform: z.string(),
  challengeType: z.string(),
  balance: z.number(),
});

const userCertificateSchema = z.object({
  id: z.number(),
  type: z.enum(['performance', 'payout', 'funding', 'passverification', 'passchallenge']),
  certificateUrl: z.string(),
  accountId: z.string(),
  issuedAt: z.string(),
  // Performance data fields
  profitAchieved: z.string().nullable().optional(),
  timeToPass: z.string().nullable().optional(),
  maxDrawdown: z.string().nullable().optional(),
  strategyUsed: z.string().nullable().optional(),
  challengeType: z.string().nullable().optional(),
  riskProfile: z.string().nullable().optional(),
  account: certificateAccountSchema,
});

const userCertificatesResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
  data: z.object({
    userId: z.number(),
    totalCertificates: z.number(),
    certificates: z.array(userCertificateSchema),
  }),
});

// Types
export type UserCertificate = z.infer<typeof userCertificateSchema>;
export type CertificateAccount = z.infer<typeof certificateAccountSchema>;
export type UserCertificatesResponse = z.infer<typeof userCertificatesResponseSchema>;

// Filter parameters for certificates
export interface CertificateFilters {
  type?: 'performance' | 'payout' | 'funding' | 'passverification' | 'passchallenge';
  types?: string; // For multiple types like "performance,funding"
}

// Response schema for email-based certificate fetching
const certificatesByEmailResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
  data: z.object({
    userId: z.number(),
    userEmail: z.string(),
    userName: z.string(),
    totalCertificates: z.number(),
    certificates: z.array(userCertificateSchema),
  }),
});

export type CertificatesByEmailResponse = z.infer<typeof certificatesByEmailResponseSchema>;

// Response schema for hash-based certificate verification
const certificateVerifyResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
  data: z.object({
    verified: z.boolean(),
    certificate: z.object({
      id: z.number(),
      type: z.string(),
      accountId: z.string(),
      certificateUrl: z.string(),
      issuedAt: z.string(),
      userName: z.string().optional(), // Name from the certificate
      // Performance data fields
      profitAchieved: z.string().nullable().optional(),
      timeToPass: z.string().nullable().optional(),
      maxDrawdown: z.string().nullable().optional(),
      strategyUsed: z.string().nullable().optional(),
      challengeType: z.string().nullable().optional(),
      riskProfile: z.string().nullable().optional(),
      account: z.object({
        accountName: z.string(),
        platform: z.string(),
        challengeType: z.string(),
        holder: z.object({
          name: z.string(),
          email: z.string(),
        }).nullable(),
      }).nullable(),
    }),
  }),
});

export type CertificateVerifyResponse = z.infer<typeof certificateVerifyResponseSchema>;

// Certificate API endpoints
export const certificates = {
  getUserCertificates: async (userId: number, filters?: CertificateFilters) => {
    const params = new URLSearchParams();
    
    if (filters?.type) {
      params.append('type', filters.type);
    }
    
    if (filters?.types) {
      params.append('types', filters.types);
    }
    
    const queryString = params.toString();
    const url = `/certificate/user/${userId}${queryString ? `?${queryString}` : ''}`;
    
    const response = await apiClient.get(url);
    const validated = userCertificatesResponseSchema.parse(response.data);
    return validated;
  },

  getCertificatesByEmail: async (email: string) => {
    const response = await apiClient.post('/certificate/verify-by-email', { email });
    const validated = certificatesByEmailResponseSchema.parse(response.data);
    return validated;
  },

  verifyCertificateByHash: async (hash: string) => {
    const response = await apiClient.get(`/certificate/verify?hash=${hash}`);
    const validated = certificateVerifyResponseSchema.parse(response.data);
    return validated;
  },
};